import is from '@sindresorhus/is';
import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

type UseDropdownKeyboardOptions<T> = {
    readonly cursor: number;
    readonly isOpen: boolean;
    readonly onSelect: (value: T) => void;
    readonly onSetCursor: (cursor: number | ((prevState: number) => number)) => void;
    readonly onSetOpen: (open: boolean) => void;
    readonly options: readonly { readonly disabled?: boolean; readonly value: T }[];
};

export const useDropdownKeyboard = <T extends string>({
    cursor,
    isOpen,
    onSelect,
    onSetCursor,
    onSetOpen,
    options,
}: UseDropdownKeyboardOptions<T>): { readonly keyDownListener: (e: KeyboardEvent<HTMLElement>) => void } => {
    const keyDownListener = useCallback(
        (e: KeyboardEvent<HTMLElement>): void => {
            if (!isOpen || is.emptyArray(options)) {
                return;
            }

            switch (e.key) {
                case 'Enter': {
                    e.preventDefault();
                    e.stopPropagation();
                    const currentOption = options[cursor];
                    if (!is.undefined(currentOption) && currentOption.disabled !== true) {
                        onSelect(currentOption.value);
                    }
                    break;
                }
                case 'ArrowUp': {
                    e.preventDefault();
                    onSetCursor((prevState) => {
                        let newCursor = prevState > 0 ? prevState - 1 : options.length - 1;

                        while (options[newCursor]?.disabled === true && newCursor !== prevState) {
                            newCursor = newCursor > 0 ? newCursor - 1 : options.length - 1;
                        }

                        return newCursor;
                    });
                    break;
                }
                case 'ArrowDown': {
                    e.preventDefault();
                    onSetCursor((prevState) => {
                        let newCursor = prevState === options.length - 1 ? 0 : prevState + 1;

                        while (options[newCursor]?.disabled === true && newCursor !== prevState) {
                            newCursor = newCursor === options.length - 1 ? 0 : newCursor + 1;
                        }

                        return newCursor;
                    });
                    break;
                }
                case 'Escape': {
                    e.preventDefault();
                    onSetOpen(false);
                    break;
                }
                default:
                    break;
            }
        },
        [isOpen, options, cursor, onSelect, onSetCursor, onSetOpen],
    );

    return { keyDownListener };
};
